import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { ORG_TEAM_RUBRIC_CUSTOM_SCHEME_ID } from 'js/utils/orgTeamRubricScheme';
import { patchForSchemeChange } from './orgRubricSchemesUtils';

const OrgRubricSchemesContext = createContext(null);

export function OrgRubricSchemesProvider({
  organization,
  rubricId,
  rubricDocument,
  schemesPayload,
  onPersistTeamScheme,
  children,
}) {
  const [saving, setSaving] = useState(false);
  const [optimisticByTeamId, setOptimisticByTeamId] = useState({});

  const teamRowById = useMemo(() => {
    const base = new Map(
      (schemesPayload?.teams ?? []).map((row) => [row.team_id, row])
    );
    for (const [teamId, patch] of Object.entries(optimisticByTeamId)) {
      const prev = base.get(Number(teamId)) ?? base.get(teamId);
      if (prev) {
        base.set(Number(teamId), { ...prev, ...patch });
      }
    }
    return base;
  }, [schemesPayload, optimisticByTeamId]);

  const onSchemeChange = useCallback(
    async (teamId, nextSelectValue) => {
      const row = teamRowById.get(teamId);
      const body = patchForSchemeChange(row, nextSelectValue, rubricDocument);
      if (!body || !rubricId || !organization?.id) {
        return;
      }

      setSaving(true);
      const optimistic =
        body.inherit === true
          ? {
              org_override: false,
              org_weight_scheme: null,
              effective_weight_scheme: row?.team_weight_scheme ?? null,
            }
          : {
              org_override: true,
              org_weight_scheme:
                body.weight_scheme == null ? null : body.weight_scheme,
              effective_weight_scheme:
                body.weight_scheme == null ? null : body.weight_scheme,
              ...(body.weight_scheme === ORG_TEAM_RUBRIC_CUSTOM_SCHEME_ID
                ? { custom_weights: row?.custom_weights ?? {} }
                : {}),
            };

      setOptimisticByTeamId((prev) => ({ ...prev, [teamId]: optimistic }));

      try {
        await onPersistTeamScheme(organization.id, rubricId, teamId, body);
        setOptimisticByTeamId((prev) => {
          const next = { ...prev };
          delete next[teamId];
          return next;
        });
      } catch {
        setOptimisticByTeamId((prev) => {
          const next = { ...prev };
          delete next[teamId];
          return next;
        });
      } finally {
        setSaving(false);
      }
    },
    [
      teamRowById,
      rubricId,
      rubricDocument,
      organization?.id,
      onPersistTeamScheme,
    ]
  );

  const value = useMemo(
    () => ({
      teamRowById,
      saving,
      onSchemeChange,
    }),
    [teamRowById, saving, onSchemeChange]
  );

  return (
    <OrgRubricSchemesContext.Provider value={value}>
      {children}
    </OrgRubricSchemesContext.Provider>
  );
}

export function useOrgRubricSchemes() {
  const ctx = useContext(OrgRubricSchemesContext);
  if (!ctx) {
    throw new Error(
      'useOrgRubricSchemes must be used within OrgRubricSchemesProvider'
    );
  }
  return ctx;
}
